import {
  Directive,
  Input,
  ElementRef,
  Injector,
  OnInit,
  OnDestroy,
  Optional,
  Self
} from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { fromEvent, Subject, Subscription, timer } from 'rxjs';
import { takeUntil, filter, tap } from 'rxjs/operators';
import { TooltipDrawerComponent } from '../ui/components/tooltip-drawer/tooltip-drawer.component';
import { SmartTooltipDirective } from './smart-tooltip.directive';

type TooltipTrigger = 'hover' | 'click';
type TooltipPosition = 'up' | 'down' | 'left' | 'right';
const baseLeftPosition: ConnectedPosition = {
  originX: 'start', originY: 'center',
  overlayX: 'end', overlayY: 'center',
  offsetX: -8
};
const baseRightPosition: ConnectedPosition = {
  originX: 'end', originY: 'center',
  overlayX: 'start', overlayY: 'center',
  offsetX: 8
};
const baseUpPosition: ConnectedPosition = {
  originX: 'start', originY: 'top',
  overlayX: 'start', overlayY: 'bottom',
  offsetY: -8
};
const baseDownPosition: ConnectedPosition = {
  originX: 'start', originY: 'bottom',
  overlayX: 'start', overlayY: 'top',
  offsetY: 8
};
const positionMap: Record<TooltipPosition, ConnectedPosition> = {
  up: baseUpPosition,
  down: baseDownPosition,
  left: baseLeftPosition,
  right: baseRightPosition
};
const fallbackOrder: TooltipPosition[] = ['down', 'up', 'right', 'left'];

@Directive({
  selector: '[tooltipDrawer]',
  standalone: true,
})
export class TooltipDrawerDirective implements OnInit, OnDestroy {

  @Input('tooltipDrawer') component!: any;
  @Input() tooltipData?: any;
  @Input() trigger: TooltipTrigger = 'click';
  @Input() position: TooltipPosition = 'down';
  @Input() maxWidth?: string;
  @Input() allowHtml: boolean = false;
  @Input() openDelay = 150;
  @Input() closeDelay = 150;
  @Input() tooltipClass?: string | string[];

  private overlayRef!: OverlayRef;
  private destroy$ = new Subject<void>();
  private hoverState$ = new Subject<'enter' | 'leave'>();
  private overlayHoverState$ = new Subject<'enter' | 'leave'>();
  private scrollSub?: Subscription;
  //protected componentRef?: ComponentRef<any>;

  constructor(
    private el: ElementRef<HTMLElement>,
    private overlay: Overlay,
    private injector: Injector,
    @Optional() @Self() private smartTooltip?: SmartTooltipDirective
  ) { }

  ngOnInit(): void {
    if (this.trigger === 'hover') {
      this.initHover();
    } else {
      this.initClick();
    }
  }

  private initHover() {
    const el = this.el.nativeElement;
    fromEvent(el, 'mouseenter')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.hoverState$.next('enter'));
    fromEvent(el, 'mouseleave')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.hoverState$.next('leave'));

    this.hoverState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        if (state === 'enter') {
          this.handleOpenWithDelay();
        } else {
          this.handleCloseWithDelay();
        }
      });
  }

  private handleOpenWithDelay() {
    timer(this.openDelay)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.hoverState$.pipe(filter(s => s === 'leave')))
      )
      .subscribe(() => this.open());
  }

  private handleCloseWithDelay() {
    timer(this.closeDelay)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.hoverState$.pipe(filter(s => s === 'enter'))),
        takeUntil(this.overlayHoverState$.pipe(filter(s => s === 'enter')))
      )
      .subscribe(() => this.close());
  }

  private initClick() {
    const el = this.el.nativeElement;

    fromEvent(el, 'click')
      .pipe(
        takeUntil(this.destroy$),
        tap(e => { if (this.isTooltipEnabled()) e.stopPropagation() })
      )
      .subscribe(() => this.toggle());

    fromEvent(document, 'click')
      .pipe(
        takeUntil(this.destroy$),
        filter((event: any) => {
          return (
            this.overlayRef?.hasAttached() &&
            !el.contains(event.target) &&
            !this.overlayRef.overlayElement.contains(event.target)
          );
        })
      )
      .subscribe(() => this.close());
  }

  private open() {
    if (!this.isTooltipEnabled() || !this.hasContent()) return;
    if (this.overlayRef?.hasAttached()) return;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.el)
      .withPositions(this.getPositions())
      .withPush(true)
      .withFlexibleDimensions(true)
      .withViewportMargin(8);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close()
    });

    const containerPortal = new ComponentPortal(TooltipDrawerComponent, null, this.injector);
    const containerRef = this.overlayRef.attach(containerPortal);
    containerRef.instance.setConfig({
      maxWidth: this.maxWidth,
      allowHtml: this.allowHtml,
      tooltipClass: this.tooltipClass
    });
    //this.componentRef =
    containerRef.instance.attachContent(
      this.component,
      this.tooltipData
    );

    this.afterOpen();
  }

  private afterOpen() {
    if (this.trigger === 'hover') {
      this.bindOverlayHoverEvents();
    }

    this.bindGlobalScrollClose();
  }

  private bindOverlayHoverEvents() {
    const overlayEl = this.overlayRef.overlayElement;

    fromEvent(overlayEl, 'mouseenter')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.overlayHoverState$.next('enter'));

    fromEvent(overlayEl, 'mouseleave')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.overlayHoverState$.next('leave');
        this.handleCloseWithDelay();
      });
  }

  private close() {
    if (!this.overlayRef?.hasAttached()) return;
    this.overlayRef?.detach();

    this.scrollSub?.unsubscribe();
    this.scrollSub = undefined;
  }

  private toggle() {
    this.overlayRef?.hasAttached() ? this.close() : this.open();
  }

  private bindGlobalScrollClose() {
    this.scrollSub?.unsubscribe();

    this.scrollSub = fromEvent(document, 'scroll', {
      capture: true,
      passive: true
    }).pipe(takeUntil(this.destroy$))
      .subscribe((event: Event) => {
        const target = event.target as Node;
        if (this.isInsideTooltipOrHost(target)) return;
        this.close();
      });
  }

  private isInsideTooltipOrHost(target: EventTarget | null): boolean {
    if (!target) return false;
    const node = target as Node;
    const host = this.el.nativeElement;
    const overlay = this.overlayRef?.overlayElement;
    return host.contains(node) || overlay?.contains(node);
  }

  private isTooltipEnabled(): boolean {
    if (!this.smartTooltip) return true;
    return this.smartTooltip.isEnabled();
  }

  private hasContent(): boolean {
    if (!this.component) return false;
    if (typeof this.component === 'string') return this.component.trim().length > 0;
    return true;
  }

  private getPositions(): ConnectedPosition[] {
    const preferred = positionMap[this.position] ?? baseDownPosition;
    const fallbacks = fallbackOrder
      .filter(p => p !== this.position)
      .map(p => positionMap[p]);
    return [preferred, ...fallbacks];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroy$.unsubscribe();
    this.overlayRef?.dispose();
  }
}
